import React, { useEffect, useRef } from 'react';
import {
  ArrayInput,
  SimpleFormIterator,
  DateInput,
  TextInput,
  BooleanInput,
  NumberInput,
  required,
} from 'react-admin';
import { Button } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import moment from 'moment';
import { generateSessionID, calculateSessionDates } from '../../../utils/eventFormUtils';
import { ScopedIteratorFields } from '../event-utils/ScopedIteratorFields';

/**
 * Session Manager Component
 * Handles session creation and management for multi-date events
 */
export const SessionManager = ({ disabled = false, isEdit = false }) => {
  return (
    <React.Fragment>
      <NumberInput
        source="duration"
        label="Event Duration (minutes)"
        fullWidth
        validate={required()}
        disabled={disabled}
      />

      <SessionDateCalculator />

      <ArrayInput source="sessions" defaultValue={[]} label="Sessions" validate={required()}>
        <SimpleFormIterator
          disableReordering
          disableRemove={disabled}
          disableAdd={disabled}
          className="sessionEventSessionsArray"
        >
          <AutoAdvanceSessionDateInput disabled={disabled} isEdit={isEdit} />

          <ArrayInput source="times" defaultValue={[]} label="Times">
            <SimpleFormIterator
              disableReordering
              disableRemove={disabled}
              disableAdd={disabled}
              inline
            >
              <ScopedIteratorFields>
                {({ getSource }) => (
                  <React.Fragment>
                    <TextInput
                      source={getSource('id')}
                      label="Session ID"
                      validate={required()}
                      fullWidth
                      disabled
                      defaultValue={generateSessionID()}
                    />
                    <TextInput
                      source={getSource('time')}
                      fullWidth
                      validate={required()}
                      label="Time"
                      type="time"
                      disabled={disabled}
                      InputProps={{
                        inputProps: {
                          step: 300, // 5 min
                        },
                      }}
                    />
                    <BooleanInput
                      source={getSource('isActive')}
                      label="Active"
                      defaultValue={true}
                      disabled={disabled}
                    />
                  </React.Fragment>
                )}
              </ScopedIteratorFields>
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleFormIterator>
      </ArrayInput>
    </React.Fragment>
  );
};

const AutoAdvanceSessionDateInput = ({ disabled = false, isEdit = false }) => {
  const inputRef = useRef(null);

  const focusFirstTimeInput = React.useCallback(() => {
    const currentInput = inputRef.current;
    const row = currentInput?.closest('li');
    const timeInput = row?.querySelector('input[type="time"]');
    timeInput?.focus();
  }, []);

  const handleChange = (event) => {
    if (event.target.value?.length === 10) {
      window.setTimeout(focusFirstTimeInput, 0);
    }
  };

  return (
    <DateInput
      source="startdate"
      fullWidth
      label="Date"
      disabled={disabled}
      validate={[
        required(),
        (value) => {
          if (!value) return undefined;
          if (!moment(value).isValid()) return 'Please enter a valid date.';
          if (!isEdit && moment(value).isBefore(moment())) {
            return 'Start date should be after today.';
          }
          return undefined;
        },
      ]}
      onChange={handleChange}
      InputProps={{ inputRef }}
    />
  );
};

/**
 * Session Date Calculator Component
 * Auto-calculates start and end dates based on sessions
 */
const SessionDateCalculator = () => {
  const { setValue } = useFormContext();
  const sessions = useWatch({ name: 'sessions' });
  const lastCalculatedRef = useRef(null);

  useEffect(() => {
    if (sessions && sessions.length > 0) {
      // Only recalculate if sessions have actually changed
      const sessionsString = JSON.stringify(sessions);
      if (lastCalculatedRef.current !== sessionsString) {
        const { startdate, enddate } = calculateSessionDates(sessions);
        if (startdate) setValue('startdate', startdate);
        if (enddate) setValue('enddate', enddate);
        lastCalculatedRef.current = sessionsString;
      }
    }
  }, [sessions, setValue]);

  return null; // This component doesn't render anything
};
